import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';
import DatePicker from '../../../components/DatePicker';
import { X, ChevronDown, RotateCw } from 'lucide-react';

const formatDateToYYYYMMDD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const ManualMembershipsTab = ({ toast }) => {
  const { hasPermission, hasRole } = useAuth();

  // Presets and query filters
  const [manualPreset, setManualPreset] = useState('all');
  const [manualStartDate, setManualStartDate] = useState('');
  const [manualEndDate, setManualEndDate] = useState('');
  const [manualSearchQuery, setManualSearchQuery] = useState('');

  // Pagination states
  const [manualPage, setManualPageState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_page_manual_memberships');
      return saved ? Math.max(0, parseInt(saved, 10)) : 0;
    } catch (e) {
      return 0;
    }
  });

  const setManualPage = useCallback((valOrFn) => {
    setManualPageState(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        sessionStorage.setItem('admin_page_manual_memberships', String(next));
      } catch (e) {}
      return next;
    });
  }, []);

  const [manualPageSize, setManualPageSize] = useState(25);

  // Data states
  const [manualMemberships, setManualMemberships] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auto-complete and Grant modal states
  const [showGrantManualModal, setShowGrantManualModal] = useState(false);
  const [manualForm, setManualForm] = useState({
    vendor_email: '',
    plan_name: 'PROFESSIONAL',
    amount: 12000,
    reason: ''
  });
  const [manualFormError, setManualFormError] = useState('');
  const [manualFormLoading, setManualFormLoading] = useState(false);
  const [selectedVendorDetail, setSelectedVendorDetail] = useState(null);
  const [isConfirmingGrant, setIsConfirmingGrant] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');

  // Edit manual grant states
  const [showEditManualModal, setShowEditManualModal] = useState(false);
  const [editingManualLog, setEditingManualLog] = useState(null);
  const [editManualForm, setEditManualForm] = useState({
    plan_name: 'PROFESSIONAL',
    amount: 12000,
    end_date: '',
    reason: ''
  });
  const [editManualFormError, setEditManualFormError] = useState('');
  const [editManualFormLoading, setEditManualFormLoading] = useState(false);

  const [planPrices, setPlanPrices] = useState({
    company: { PROFESSIONAL: 12000, PREMIUM: 25000 },
    individual: { PROFESSIONAL: 4000, PREMIUM: 7000 }
  });

  const fetchPlanPrices = useCallback(async () => {
    try {
      const res = await vendorService.getPublicMembershipPlans();
      if (res.success && res.data) {
        const cPlans = res.data.company_plans || res.data.plans;
        const iPlans = res.data.individual_plans;
        setPlanPrices({
          company: {
            PROFESSIONAL: cPlans?.PROFESSIONAL?.amount_inr || 12000,
            PREMIUM: cPlans?.PREMIUM?.amount_inr || 25000,
          },
          individual: {
            PROFESSIONAL: iPlans?.PROFESSIONAL?.amount_inr || 4000,
            PREMIUM: iPlans?.PREMIUM?.amount_inr || 7000,
          }
        });
      }
    } catch (e) {
      console.error('Error fetching plan prices in manual modal:', e);
    }
  }, []);

  // Graph interactive states
  const [hoveredManualIndex, setHoveredManualIndex] = useState(null);

  const fetchManualMemberships = useCallback(async () => {
    if (!hasPermission('MANAGE_MEMBERSHIPS')) return;
    const isManualAdmin = hasRole && hasRole('ADMIN');
    if (!isManualAdmin) return;
    setLoading(true);
    try {
      const res = await vendorService.getAdminManualMemberships();
      if (res.success && res.data) {
        setManualMemberships(res.data.logs || []);
      } else {
        toast.error(res.error || 'Failed to fetch manual memberships');
      }
    } catch (err) {
      toast.error('An error occurred while fetching manual memberships.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, hasRole, toast]);

  const fetchAllVendors = useCallback(async () => {
    if (!hasPermission('MANAGE_MEMBERSHIPS')) return;
    const isManualAdmin = hasRole && hasRole('ADMIN');
    if (!isManualAdmin) return;
    try {
      const res = await vendorService.getAdminAllVendors();
      if (res.success && res.data) {
        setAllVendors(res.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [hasPermission, hasRole]);

  useEffect(() => {
    fetchManualMemberships();
    fetchAllVendors();
    fetchPlanPrices();
  }, [fetchManualMemberships, fetchAllVendors, fetchPlanPrices]);

  const handleManualPreset = (preset) => {
    const today = new Date();
    let start = '';
    let end = '';

    if (preset === '7days') {
      const d = new Date(today);
      d.setDate(today.getDate() - 6);
      start = formatDateToYYYYMMDD(d);
      end = formatDateToYYYYMMDD(today);
    } else if (preset === '30days') {
      const d = new Date(today);
      d.setDate(today.getDate() - 29);
      start = formatDateToYYYYMMDD(d);
      end = formatDateToYYYYMMDD(today);
    } else if (preset === 'thismonth') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      start = formatDateToYYYYMMDD(firstDay);
      end = formatDateToYYYYMMDD(today);
    } else if (preset === '6months') {
      const d = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      start = formatDateToYYYYMMDD(d);
      end = formatDateToYYYYMMDD(today);
    } else if (preset === 'all') {
      start = '';
      end = '';
    }

    setManualStartDate(start);
    setManualEndDate(end);
    setManualPreset(preset);
    setManualPage(0);
  };

  const handleProceedToConfirmation = (e) => {
    if (e) e.preventDefault();
    setManualFormError('');

    if (!manualForm.vendor_email) {
      setManualFormError('Please select a vendor.');
      return;
    }
    if (!manualForm.amount || manualForm.amount < 0) {
      setManualFormError('Please enter a valid amount.');
      return;
    }

    setIsConfirmingGrant(true);
  };

  const handleConfirmGrant = async () => {
    if (manualFormLoading) return;
    setManualFormError('');
    setManualFormLoading(true);

    const payload = {
      vendor_email: manualForm.vendor_email,
      plan_name: manualForm.plan_name,
      amount: parseInt(manualForm.amount),
      reason: manualForm.reason
    };

    try {
      const res = await vendorService.createManualMembership(payload);
      setManualFormLoading(false);
      if (res.success) {
        toast.success(`Successfully granted manual membership to ${manualForm.vendor_email}`);
        setShowGrantManualModal(false);
        setManualForm({ vendor_email: '', plan_name: 'PROFESSIONAL', amount: 12000, reason: '' });
        setSelectedVendorDetail(null);
        setIsConfirmingGrant(false);
        fetchManualMemberships();
      } else {
        setManualFormError(res.error || 'Failed to grant membership');
        toast.error(res.error || 'Failed to grant membership');
      }
    } catch (err) {
      setManualFormLoading(false);
      setManualFormError('An error occurred during VIP provisioning.');
      toast.error('An error occurred during VIP provisioning.');
      console.error(err);
    }
  };

  const handleEditManualSubmit = async (e) => {
    e.preventDefault();
    setEditManualFormError('');
    setEditManualFormLoading(true);

    if (!editManualForm.amount || editManualForm.amount < 0) {
      setEditManualFormError('Please enter a valid amount.');
      setEditManualFormLoading(false);
      return;
    }
    if (!editManualForm.end_date) {
      setEditManualFormError('Please select an expiry date.');
      setEditManualFormLoading(false);
      return;
    }

    const payload = {
      plan_name: editManualForm.plan_name,
      amount: parseInt(editManualForm.amount),
      end_date: new Date(editManualForm.end_date).toISOString(),
      reason: editManualForm.reason
    };

    try {
      const res = await vendorService.updateManualMembership(editingManualLog.id, payload);
      setEditManualFormLoading(false);
      if (res.success) {
        toast.success(`Successfully updated manual membership for ${editingManualLog.vendor_email}`);
        setShowEditManualModal(false);
        setEditingManualLog(null);
        setEditManualForm({
          plan_name: 'PROFESSIONAL',
          amount: 12000,
          end_date: '',
          reason: ''
        });
        fetchManualMemberships();
      } else {
        setEditManualFormError(res.error || 'Failed to update membership');
        toast.error(res.error || 'Failed to update membership');
      }
    } catch (err) {
      setEditManualFormLoading(false);
      setEditManualFormError('An error occurred during membership update.');
      toast.error('An error occurred during membership update.');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center py-24 select-none">
        <div className="w-9 h-9 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-sm text-gray-500 font-semibold mt-3">Loading custom billing registry...</span>
      </div>
    );
  }

  const dateFilteredLogs = manualMemberships.filter(log => {
    if (!log.start_date) return true;
    const logDate = new Date(log.start_date);
    if (isNaN(logDate.getTime())) return true;
    if (manualStartDate) {
      const start = new Date(manualStartDate);
      start.setHours(0, 0, 0, 0);
      if (logDate < start) return false;
    }
    if (manualEndDate) {
      const end = new Date(manualEndDate);
      end.setHours(23, 59, 59, 999);
      if (logDate > end) return false;
    }
    return true;
  });

  const filteredLogs = dateFilteredLogs.filter(log => {
    const query = manualSearchQuery.toLowerCase();
    return (
      log.vendor_email.toLowerCase().includes(query) ||
      log.company_name.toLowerCase().includes(query) ||
      (log.admin_email && log.admin_email.toLowerCase().includes(query)) ||
      (log.reason && log.reason.toLowerCase().includes(query))
    );
  });

  const manualTotalPages = Math.ceil(filteredLogs.length / manualPageSize);
  const safeManualPage = Math.min(manualPage, Math.max(0, manualTotalPages - 1));
  const displayedLogs = filteredLogs.slice(safeManualPage * manualPageSize, (safeManualPage + 1) * manualPageSize);

  const totalRevenue = dateFilteredLogs.reduce((sum, log) => sum + (log.amount || 0), 0);
  const totalCount = dateFilteredLogs.length;
  const averagePrice = totalCount > 0 ? Math.round(totalRevenue / totalCount) : 0;
  const activeManualCount = dateFilteredLogs.filter(log => !log.is_expired).length;

  const profCount = dateFilteredLogs.filter(log => log.plan_name === 'PROFESSIONAL').length;
  const premCount = dateFilteredLogs.filter(log => log.plan_name === 'PREMIUM').length;
  const totalSplitCount = profCount + premCount;
  const profPct = totalSplitCount > 0 ? (profCount / totalSplitCount) * 100 : 0;
  const premPct = totalSplitCount > 0 ? (premCount / totalSplitCount) * 100 : 0;

  const getManualTrendIntervals = () => {
    let start, end;
    if (manualStartDate && manualEndDate) {
      let startD = new Date(manualStartDate);
      let endD = new Date(manualEndDate);
      if (startD > endD) {
        const temp = startD;
        startD = endD;
        endD = temp;
      }
      start = startD;
      start.setHours(0, 0, 0, 0);
      end = endD;
      end.setHours(23, 59, 59, 999);
    } else {
      const now = new Date();
      start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      end.setHours(23, 59, 59, 999);
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const intervals = [];

    if (diffDays <= 31) {
      const current = new Date(start);
      while (current <= end) {
        const dayStart = new Date(current);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(current);
        dayEnd.setHours(23, 59, 59, 999);
        intervals.push({
          name: current.toLocaleDateString('default', { day: '2-digit', month: 'short' }),
          year: current.getFullYear(),
          monthIndex: current.getMonth(),
          start: dayStart,
          end: dayEnd,
          revenue: 0,
          count: 0
        });
        current.setDate(current.getDate() + 1);
      }
    } else {
      const current = new Date(start.getFullYear(), start.getMonth(), 1);
      const targetEnd = new Date(end.getFullYear(), end.getMonth(), 1);
      while (current <= targetEnd) {
        const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
        monthStart.setHours(0, 0, 0, 0);
        const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
        monthEnd.setHours(23, 59, 59, 999);
        const intervalStart = monthStart < start ? start : monthStart;
        const intervalEnd = monthEnd > end ? end : monthEnd;
        intervals.push({
          name: current.toLocaleString('default', { month: 'short' }) + (current.getFullYear() !== new Date().getFullYear() ? ` '${String(current.getFullYear()).slice(-2)}` : ''),
          year: current.getFullYear(),
          monthIndex: current.getMonth(),
          start: intervalStart,
          end: intervalEnd,
          revenue: 0,
          count: 0
        });
        current.setMonth(current.getMonth() + 1);
      }
    }
    return intervals;
  };

  const getManualTrendData = () => {
    const intervals = getManualTrendIntervals();
    dateFilteredLogs.forEach(m => {
      if (!m.start_date || m.plan_name === 'FREE') return;
      const mDate = new Date(m.start_date);
      intervals.forEach(interval => {
        if (mDate >= interval.start && mDate <= interval.end) {
          interval.revenue += m.amount || 0;
          interval.count += 1;
        }
      });
    });
    return intervals;
  };

  const chartData = getManualTrendData();
  const N = chartData.length;
  const maxRevenue = Math.max(...chartData.map(d => d.revenue), 10000) * 1.15;
  const points = chartData.map((d, idx) => {
    const x = 55 + (idx * (365 / Math.max(N - 1, 1)));
    const y = 180 - ((d.revenue / maxRevenue) * 140);
    return { x, y, month: d.name || d.month, revenue: d.revenue, count: d.count };
  });

  let areaPath = '';
  let linePath = '';
  if (N > 0) {
    if (N === 1) {
      areaPath = `M 55 180 L 55 ${points[0].y} L 420 ${points[0].y} L 420 180 Z`;
      linePath = `M 55 ${points[0].y} L 420 ${points[0].y}`;
    } else {
      linePath = `M ${points[0].x} ${points[0].y}`;
      areaPath = `M ${points[0].x} 180 L ${points[0].x} ${points[0].y}`;
      for (let i = 1; i < N; i++) {
        linePath += ` L ${points[i].x} ${points[i].y}`;
        areaPath += ` L ${points[i].x} ${points[i].y}`;
      }
      areaPath += ` L ${points[N - 1].x} 180 Z`;
    }
  }

  const formatReadableDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
      const d = new Date(dateInput);
      if (isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const calculateNewEndDate = () => {
    const isSamePlan = selectedVendorDetail && selectedVendorDetail.current_plan === manualForm.plan_name;
    const isActive = selectedVendorDetail && selectedVendorDetail.current_status === 'ACTIVE';

    let baseDate = new Date();
    if (isSamePlan && isActive && selectedVendorDetail.end_date) {
      baseDate = new Date(selectedVendorDetail.end_date);
    }

    const newDate = new Date(baseDate.getTime() + 365 * 24 * 60 * 60 * 1000);
    return newDate;
  };

  const renderConfirmationMessage = () => {
    if (!selectedVendorDetail) return null;

    const currentPlan = selectedVendorDetail.current_plan || 'FREE';
    const newPlan = manualForm.plan_name;
    const isSamePlan = currentPlan === newPlan;
    const isActive = selectedVendorDetail.current_status === 'ACTIVE';

    const formattedCurrentEnd = selectedVendorDetail.end_date
      ? formatReadableDate(selectedVendorDetail.end_date)
      : 'N/A';

    const formattedNewEnd = formatReadableDate(calculateNewEndDate());

    if (isActive && currentPlan !== 'FREE') {
      if (isSamePlan) {
        return (
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs font-semibold space-y-2.5">
              <p className="font-extrabold text-sm text-amber-800">
                Confirm Active Plan Renewal
              </p>
              <p>
                Vendor <strong>{selectedVendorDetail.company_name}</strong> currently has an active <strong>{currentPlan}</strong> membership expiring on <span className="underline font-bold">{formattedCurrentEnd}</span>.
              </p>
              <p>
                Renewing this plan will extend their access by exactly 1 year from their current expiration. The new expiration date will be <span className="underline font-bold text-amber-950 bg-amber-100/50 px-1 rounded">{formattedNewEnd}</span>.
              </p>
            </div>
            <p className="text-xs text-slate-500 font-bold text-center">Are you sure you want to grant this manual renewal?</p>
          </div>
        );
      } else {
        return (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-blue-900 text-xs font-semibold space-y-2.5">
              <p className="font-extrabold text-sm text-blue-800">
                Confirm Active Plan Transition
              </p>
              <p>
                Vendor <strong>{selectedVendorDetail.company_name}</strong> currently has an active <strong>{currentPlan}</strong> membership expiring on <span className="underline font-bold">{formattedCurrentEnd}</span>.
              </p>
              <p>
                Changing their plan to <strong>{newPlan}</strong> will replace their active plan immediately. The new 1-year plan starts today and will expire on <span className="underline font-bold text-blue-950 bg-blue-100/50 px-1 rounded">{formattedNewEnd}</span>.
              </p>
            </div>
            <p className="text-xs text-slate-500 font-bold text-center">Are you sure you want to override their current active membership plan?</p>
          </div>
        );
      }
    } else {
      return (
        <div className="space-y-4">
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-900 text-xs font-semibold space-y-2.5">
            <p className="font-extrabold text-sm text-emerald-800">
              Confirm New VIP Membership
            </p>
            <p>
              Vendor <strong>{selectedVendorDetail.company_name}</strong> does not have an active paid membership.
            </p>
            <p>
              Granting a new 1-Year <strong>{newPlan}</strong> membership will activate immediately and will expire on <span className="underline font-bold text-emerald-950 bg-emerald-100/50 px-1 rounded">{formattedNewEnd}</span>.
            </p>
          </div>
          <p className="text-xs text-slate-500 font-bold text-center">Are you sure you want to grant this VIP membership?</p>
        </div>
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm text-left">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Manual Memberships & Custom Billing</h2>
          <p className="text-xs text-gray-400 mt-1 font-medium">Provision VIP memberships directly to vendors with customized pricing and custom durations.</p>
        </div>
        <button
          onClick={() => {
            setShowGrantManualModal(true);
            setManualFormError('');
          }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#2ab391] hover:bg-[#229b7c] active:scale-98 text-white text-sm font-semibold rounded-xl transition-all shadow-md shadow-[#2ab391]/10 cursor-pointer"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          Grant Manual Plan
        </button>
      </div>

      {/* Date Range Filter Panel */}
      <div className="bg-white border border-gray-200/80 rounded-2xl p-5 shadow-xs flex flex-col xl:flex-row xl:items-center justify-between gap-5 select-none animate-in fade-in duration-200 text-left">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-extrabold text-gray-800">Manual Provisioning Filter</h2>
          <p className="text-xs text-gray-400 font-semibold">Filter statistics, sales trend charts, donut ratios, and records</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {/* Presets */}
          <div className="flex flex-wrap bg-slate-50 border border-slate-100 rounded-xl p-1 gap-1">
            {[
              { id: '7days', label: '7 Days' },
              { id: '30days', label: '30 Days' },
              { id: 'thismonth', label: 'This Month' },
              { id: '6months', label: '6 Months' },
              { id: 'all', label: 'All Time' }
            ].map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleManualPreset(opt.id)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-extrabold uppercase tracking-wider transition-all cursor-pointer ${manualPreset === opt.id
                  ? 'bg-[#2ab391] text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100/50'
                  }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Custom Date Range Picker */}
          <div className="flex items-center gap-2">
            <DateRangePicker
              startDate={manualStartDate}
              endDate={manualEndDate}
              onChange={(start, end) => {
                setManualStartDate(start);
                setManualEndDate(end);
                setManualPreset(start || end ? 'custom' : 'all');
                setManualPage(0);
              }}
              placeholder="Select Date Range"
              className="w-56 bg-slate-50/50 hover:bg-slate-50 border-gray-300"
            />

            {(manualStartDate || manualEndDate) && (
              <button
                type="button"
                onClick={() => handleManualPreset('all')}
                className="text-[10px] font-extrabold text-rose-500 hover:text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-xl px-3 py-2 transition-all cursor-pointer ml-1"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
        {/* Total Revenue */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-800">₹{totalRevenue.toLocaleString()}</div>
            <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mt-0.5">Manual Revenue</div>
          </div>
        </div>

        {/* Total Granted */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-800">{totalCount}</div>
            <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mt-0.5">Total Granted</div>
          </div>
        </div>

        {/* Average Charged Price */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-violet-50 text-violet-600">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-800">₹{averagePrice.toLocaleString()}</div>
            <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mt-0.5">Avg Custom Price</div>
          </div>
        </div>

        {/* Active Manual count */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-amber-50 text-amber-600">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-800">{activeManualCount}</div>
            <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mt-0.5">Active Manual Plans</div>
          </div>
        </div>
      </div>

      {/* ── Section 1: Analytics Overview ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden text-left">
        {/* Section Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50/60 select-none">
          <div className="p-1.5 rounded-lg bg-[#2ab391]/10 text-[#2ab391]">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-extrabold text-gray-800">Analytics Overview</h3>
            <p className="text-[11px] text-gray-400 font-medium">Revenue trend and plan distribution for manual memberships</p>
          </div>
        </div>

        {/* Chart & Plan Split Grid */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Revenue Trend Chart */}
            <div className="lg:col-span-2">
              <div className="flex justify-between items-center mb-5 select-none">
                <div>
                  <h4 className="text-sm font-extrabold text-gray-700">Manual Sales Trend</h4>
                  <p className="text-xs text-gray-400 font-semibold mt-0.5">Revenue generated from custom discounted plans.</p>
                </div>
                <span className="text-[9px] font-bold text-[#2ab391] bg-emerald-50 px-2 py-0.5 rounded uppercase tracking-wider border border-emerald-100">
                  Log history
                </span>
              </div>

              <div className="relative h-[220px] w-full">
                <svg className="w-full h-full" viewBox="0 0 450 220" preserveAspectRatio="xMidYMid meet">
                  <defs>
                    <linearGradient id="manualAreaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2ab391" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#2ab391" stopOpacity="0.0" />
                    </linearGradient>
                  </defs>

                  {/* Grid lines */}
                  <line x1="55" y1="40" x2="420" y2="40" stroke="#f3f4f6" strokeDasharray="3 3" />
                  <line x1="55" y1="110" x2="420" y2="110" stroke="#f3f4f6" strokeDasharray="3 3" />
                  <line x1="55" y1="180" x2="420" y2="180" stroke="#e5e7eb" strokeWidth="1.5" />

                  {/* Labels */}
                  <text x="45" y="44" className="text-[9px] font-bold text-gray-400 hover:text-gray-600 select-none" textAnchor="end">₹{Math.round(maxRevenue).toLocaleString()}</text>
                  <text x="45" y="114" className="text-[9px] font-bold text-gray-400 hover:text-gray-600 select-none" textAnchor="end">₹{Math.round(maxRevenue / 2).toLocaleString()}</text>
                  <text x="45" y="184" className="text-[9px] font-bold text-gray-400 hover:text-gray-600 select-none" textAnchor="end">₹0</text>

                  {/* Paths */}
                  {N > 0 && <path d={areaPath} fill="url(#manualAreaGrad)" />}
                  {N > 0 && <path d={linePath} fill="none" stroke="#2ab391" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}

                  {/* Circles */}
                  {points.map((p, idx) => (
                    <g key={idx}>
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r={hoveredManualIndex === idx ? "6" : "4.5"}
                        fill="#2ab391"
                        stroke="white"
                        strokeWidth="2"
                        className="cursor-pointer transition-all"
                        onMouseEnter={() => setHoveredManualIndex(idx)}
                        onMouseLeave={() => setHoveredManualIndex(null)}
                      />
                      {idx % Math.max(Math.round(N / 6), 1) === 0 && (
                        <text x={p.x} y="202" className="text-[9px] font-bold text-gray-400 select-none" textAnchor="middle">{p.month}</text>
                      )}
                    </g>
                  ))}
                </svg>

                {/* Hover Tooltip */}
                {hoveredManualIndex !== null && points[hoveredManualIndex] && (
                  <div
                    className="absolute bg-slate-900/95 text-white text-[10px] p-2.5 rounded-lg shadow-xl pointer-events-none z-20 border border-slate-700/50 flex flex-col gap-1 min-w-[120px]"
                    style={{
                      left: `${(points[hoveredManualIndex].x / 450) * 100}%`,
                      top: `${(points[hoveredManualIndex].y / 220) * 100 - 25}%`,
                      transform: 'translate(-50%, -100%)'
                    }}
                  >
                    <div className="font-extrabold border-b border-slate-700/50 pb-1 mb-1 text-slate-300">
                      {points[hoveredManualIndex].month}
                    </div>
                    <div className="flex justify-between gap-3 font-semibold">
                      <span className="text-slate-400">Revenue:</span>
                      <span>₹{points[hoveredManualIndex].revenue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between gap-3 font-semibold">
                      <span className="text-slate-400">Granted:</span>
                      <span>{points[hoveredManualIndex].count} plans</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Plan Distribution */}
            <div className="border-t lg:border-t-0 lg:border-l border-gray-100 pt-6 lg:pt-0 lg:pl-6 flex flex-col justify-center">
              <h4 className="text-sm font-extrabold text-gray-700 mb-1 select-none">Plan Distribution</h4>
              <p className="text-xs text-gray-400 font-semibold mb-6 select-none">Ratio of Professional vs Premium manual plans.</p>

              <div className="space-y-5">
                {/* Professional */}
                <div>
                  <div className="flex justify-between text-xs font-bold mb-1.5">
                    <span className="text-blue-600">Professional Plan</span>
                    <span className="text-gray-500">{profCount} ({Math.round(profPct)}%)</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${profPct}%` }}></div>
                  </div>
                </div>

                {/* Premium */}
                <div>
                  <div className="flex justify-between text-xs font-bold mb-1.5">
                    <span className="text-violet-600">Premium Plan</span>
                    <span className="text-gray-500">{premCount} ({Math.round(premPct)}%)</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-violet-500 rounded-full transition-all duration-500" style={{ width: `${premPct}%` }}></div>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-500 border border-gray-100 select-none">
                Total configurations: {totalSplitCount}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 2: Provisioning Registry ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden text-left">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50/60 select-none">
          <div className="flex items-center gap-3">
            <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-extrabold text-gray-800">Provisioning Registry</h3>
              <p className="text-[11px] text-gray-400 font-medium">Full audit log of all manually granted memberships</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-72">
            <input
              type="text"
              placeholder="Search email, company, notes..."
              value={manualSearchQuery}
              onChange={(e) => setManualSearchQuery(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl pl-9 pr-4 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-[#2ab391]"
            />
            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">
                <th className="p-4 pl-6">Company / Vendor</th>
                <th className="p-4">Plan Name</th>
                <th className="p-4">Charged Price</th>
                <th className="p-4">Validity Period</th>
                <th className="p-4">Granted By / Notes</th>
                <th className="p-4 pr-6">Status</th>
                <th className="p-4 pr-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
              {displayedLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-12 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400 select-none">
                      <svg className="w-10 h-10 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span className="text-sm font-semibold">No manual membership records found.</span>
                      {manualSearchQuery && <span className="text-xs">Try clearing the search query</span>}
                    </div>
                  </td>
                </tr>
              ) : (
                displayedLogs.map(log => {
                  const isInd = (log.listing_type || log.listingType) === 'INDIVIDUAL';
                  const currentPrices = isInd ? planPrices.individual : planPrices.company;
                  const standardPrice = currentPrices?.[log.plan_name] || (isInd
                    ? (log.plan_name === 'PREMIUM' ? 7000 : 4000)
                    : (log.plan_name === 'PREMIUM' ? 25000 : 12000));
                  const discount = standardPrice - log.amount;

                  return (
                    <tr key={log.id} className="hover:bg-gray-50 transition-all border-b border-gray-100 last:border-0">
                      <td className="p-4 pl-6 text-left">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-800">{log.company_name}</span>
                          <span className={`px-1.5 py-0.2 text-[9px] font-bold rounded uppercase ${
                            isInd ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}>
                            {isInd ? 'INDIVIDUAL' : 'COMPANY'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">{log.vendor_email}</div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-0.5 text-[10px] font-extrabold rounded-md ${log.plan_name === 'PREMIUM' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                          {log.plan_name}
                        </span>
                      </td>
                      <td className="p-4 text-left">
                        <div className="font-semibold text-gray-800">₹{log.amount.toLocaleString('en-IN')}</div>
                        {discount > 0 && (
                          <div className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-1.5 py-0.2 mt-1 rounded select-none">
                            Saved ₹{discount.toLocaleString('en-IN')}
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-xs font-semibold text-gray-700 text-left">
                        <div>Start: <span className="font-normal text-gray-500">{new Date(log.start_date).toLocaleDateString('en-IN')}</span></div>
                        <div className="mt-1">End: <span className="font-normal text-gray-500">{new Date(log.end_date).toLocaleDateString('en-IN')}</span></div>
                      </td>
                      <td className="p-4 text-xs font-semibold text-gray-700 max-w-[200px] text-left">
                        <div className="text-gray-400 font-normal truncate" title={log.admin_email}>By: {log.admin_email}</div>
                        {log.reason && (
                          <div className="mt-1 text-gray-600 bg-slate-50 border border-slate-100 p-1.5 rounded-lg text-[10px] font-normal leading-relaxed whitespace-pre-wrap">
                            "{log.reason}"
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-left">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold border ${!log.is_expired ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                          {!log.is_expired ? 'ACTIVE' : 'EXPIRED'}
                        </span>
                      </td>
                      <td className="p-4 pr-6 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingManualLog(log);
                            setEditManualForm({
                              plan_name: log.plan_name,
                              amount: log.amount,
                              end_date: log.end_date ? log.end_date.split('T')[0] : '',
                              reason: log.reason || ''
                            });
                            setEditManualFormError('');
                            setShowEditManualModal(true);
                          }}
                          className="p-1.5 px-3.5 bg-slate-50 hover:bg-[#2ab391]/10 text-slate-500 hover:text-[#2ab391] border border-gray-200 hover:border-[#2ab391]/20 rounded-xl transition-all cursor-pointer inline-flex items-center justify-center gap-1 text-xs font-semibold"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-505 font-medium border-t border-gray-100 pt-5 mt-6 px-6 pb-6 bg-gray-50/40 select-none">
          <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
            {filteredLogs.length === 0 ? (
              'No records available'
            ) : (
              `Showing ${safeManualPage * manualPageSize + 1} to ${Math.min((safeManualPage + 1) * manualPageSize, filteredLogs.length)} of ${filteredLogs.length} entries`
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
            <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => fetchManualMemberships()}
                title="Reload current page"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-[#2ab391] hover:border-[#2ab391]/40 hover:bg-teal-50/50 rounded-xl font-bold text-xs transition-all cursor-pointer shadow-xs"
              >
                <RotateCw size={12} />
                Reload
              </button>
              <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Show</span>
              <div className="relative">
                <select
                  value={manualPageSize}
                  onChange={(e) => {
                    setManualPageSize(Number(e.target.value));
                    setManualPage(0);
                  }}
                  className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                  <ChevronDown size={12} />
                </span>
              </div>
              <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Entries</span>
            </div>

            {manualTotalPages > 0 && (
              <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setManualPage(p => Math.max(0, p - 1))}
                  disabled={safeManualPage === 0}
                  className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
                >
                  Previous
                </button>

                <div className="hidden sm:flex items-center gap-1">
                  {getPageNumbers(safeManualPage, manualTotalPages).map((pageNum, idx) => {
                    if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                      return (
                        <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                          ...
                        </span>
                      );
                    }
                    const isActive = safeManualPage === pageNum;
                    return (
                      <button
                        key={`page-${pageNum}`}
                        type="button"
                        onClick={() => setManualPage(pageNum)}
                        className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                          ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                </div>

                <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm min-w-[70px] text-center">
                  {safeManualPage + 1} / {manualTotalPages}
                </span>

                <button
                  type="button"
                  onClick={() => setManualPage(p => Math.min(manualTotalPages - 1, p + 1))}
                  disabled={safeManualPage >= manualTotalPages - 1}
                  className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Grant Membership Modal */}
      {showGrantManualModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-200 text-left">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-100 select-none">
              <div>
                <h3 className="text-base font-bold text-gray-800">Grant Manual VIP Membership</h3>
                <p className="text-xs text-gray-400 mt-0.5">Assign an overrides plan directly to a registered vendor.</p>
              </div>
              <button
                onClick={() => {
                  setShowGrantManualModal(false);
                  setManualForm({ vendor_email: '', plan_name: 'PROFESSIONAL', amount: 12000, reason: '' });
                  setSelectedVendorDetail(null);
                  setIsConfirmingGrant(false);
                }}
                className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form Container */}
            <div className="flex flex-col max-h-[85vh]">
              {/* Scrollable Form Body */}
              <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh] flex-1">
                {isConfirmingGrant ? (
                  renderConfirmationMessage()
                ) : (
                  <>
                    {/* Vendor selection drop-down search */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Select Vendor</label>
                      {selectedVendorDetail ? (
                        <div className="space-y-3">
                          <div className="bg-gradient-to-r from-blue-50/80 to-indigo-50/50 border border-blue-200/80 p-4 rounded-2xl shadow-xs space-y-3">
                            <div className="flex justify-between items-start gap-3">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-extrabold text-gray-900 text-sm">{selectedVendorDetail.company_name}</span>
                                  <span className={`px-2 py-0.5 text-[9px] font-bold rounded-md uppercase tracking-wider ${
                                    (selectedVendorDetail.listing_type || selectedVendorDetail.listingType) === 'INDIVIDUAL'
                                      ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                      : 'bg-blue-100 text-blue-800 border border-blue-200'
                                  }`}>
                                    {(selectedVendorDetail.listing_type || selectedVendorDetail.listingType) === 'INDIVIDUAL' ? 'Individual Listing' : 'Company Listing'}
                                  </span>
                                  <span className={`px-2 py-0.5 text-[9px] font-extrabold rounded-md uppercase ${
                                    selectedVendorDetail.current_plan === 'PREMIUM'
                                      ? 'bg-violet-100 text-violet-800 border border-violet-200'
                                      : selectedVendorDetail.current_plan === 'PROFESSIONAL'
                                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                      : 'bg-gray-100 text-gray-700 border border-gray-200'
                                  }`}>
                                    {selectedVendorDetail.current_plan || 'FREE'} ({selectedVendorDetail.current_status || 'ACTIVE'})
                                  </span>
                                </div>
                                <div className="text-xs text-gray-600 font-medium">{selectedVendorDetail.email}</div>
                                {selectedVendorDetail.phone && selectedVendorDetail.phone !== 'N/A' && (
                                  <div className="text-xs text-gray-500 font-mono">Contact: {selectedVendorDetail.phone}</div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => setSelectedVendorDetail(null)}
                                className="text-xs text-blue-600 hover:text-blue-800 underline font-bold cursor-pointer shrink-0"
                              >
                                Change Vendor
                              </button>
                            </div>

                            {/* Active Plan Contextual Banner */}
                            {selectedVendorDetail.current_status === 'ACTIVE' && selectedVendorDetail.current_plan !== 'FREE' ? (
                              <div className="p-2.5 bg-white/90 border border-blue-200 rounded-xl text-xs text-blue-900 font-medium flex items-center gap-2">
                                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 shrink-0 animate-pulse"></span>
                                <span>
                                  Vendor currently has an active <strong>{selectedVendorDetail.current_plan} Plan</strong>
                                  {selectedVendorDetail.end_date ? ` (Expires on ${formatReadableDate(selectedVendorDetail.end_date)})` : ''}.
                                  Pre-selected to <strong>{selectedVendorDetail.current_plan}</strong> for renewal.
                                </span>
                              </div>
                            ) : (
                              <div className="p-2.5 bg-white/90 border border-gray-200 rounded-xl text-xs text-gray-600 font-medium">
                                Vendor is currently on the <strong>Free Plan</strong>. Select an upgrade plan below to provision VIP access.
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2 relative">
                          <input
                            type="text"
                            placeholder="Search vendor by email or company..."
                            value={vendorSearch}
                            onChange={(e) => {
                              setVendorSearch(e.target.value);
                              setManualForm({ ...manualForm, vendor_email: '' });
                            }}
                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-[#2ab391]"
                          />
                          {vendorSearch.trim().length > 0 && (
                            <div className="absolute top-full left-0 right-0 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl z-20 divide-y divide-gray-50">
                              {allVendors.filter(v =>
                                v.email.toLowerCase().includes(vendorSearch.toLowerCase()) ||
                                v.company_name.toLowerCase().includes(vendorSearch.toLowerCase())
                              ).length === 0 ? (
                                <div className="p-3 text-center text-xs text-gray-400 font-semibold">No vendors match.</div>
                              ) : (
                                allVendors.filter(v =>
                                  v.email.toLowerCase().includes(vendorSearch.toLowerCase()) ||
                                  v.company_name.toLowerCase().includes(vendorSearch.toLowerCase())
                                ).map(v => {
                                  const isIndVendor = (v.listing_type || v.listingType) === 'INDIVIDUAL';
                                  const hasActivePaidPlan = (v.current_status === 'ACTIVE' || v.current_status === 'YET_TO_EXPIRE') && (v.current_plan === 'PREMIUM' || v.current_plan === 'PROFESSIONAL');
                                  return (
                                    <button
                                      key={v.email}
                                      type="button"
                                      onClick={() => {
                                        setSelectedVendorDetail(v);
                                        const defaultPlan = hasActivePaidPlan ? v.current_plan : 'PROFESSIONAL';
                                        const defaultPrice = isIndVendor
                                          ? (planPrices.individual[defaultPlan] || (defaultPlan === 'PREMIUM' ? 7000 : 4000))
                                          : (planPrices.company[defaultPlan] || (defaultPlan === 'PREMIUM' ? 25000 : 12000));
                                        setManualForm({
                                          ...manualForm,
                                          vendor_email: v.email,
                                          plan_name: defaultPlan,
                                          amount: defaultPrice
                                        });
                                        setVendorSearch('');
                                      }}
                                      className="w-full p-3 text-left hover:bg-slate-50 transition-colors flex flex-col cursor-pointer"
                                    >
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs font-extrabold text-gray-800">{v.company_name}</span>
                                        <span className={`px-1.5 py-0.2 text-[8px] font-bold rounded uppercase ${
                                          isIndVendor ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-blue-50 text-blue-700 border border-blue-200'
                                        }`}>
                                          {isIndVendor ? 'Individual' : 'Company'}
                                        </span>
                                        {hasActivePaidPlan && (
                                          <span className={`px-1.5 py-0.2 text-[8px] font-extrabold rounded ${
                                            v.current_plan === 'PREMIUM' ? 'bg-violet-100 text-violet-700 border border-violet-200' : 'bg-blue-100 text-blue-700 border border-blue-200'
                                          }`}>
                                            {v.current_plan} (Active)
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 text-[10px] text-gray-400 font-semibold mt-0.5">
                                        <span>{v.email}</span>
                                        {v.phone && v.phone !== 'N/A' && <span>• {v.phone}</span>}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Show form fields for selected vendor */}
                    {selectedVendorDetail && (
                      <>
                        {/* Plan choice */}
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Select Override Plan</label>
                          <div className="grid grid-cols-2 gap-3">
                            {(() => {
                              const isInd = (selectedVendorDetail.listing_type || selectedVendorDetail.listingType) === 'INDIVIDUAL';
                              const currentPrices = isInd ? planPrices.individual : planPrices.company;
                              const isCurrentPlan = (planId) => selectedVendorDetail.current_status === 'ACTIVE' && selectedVendorDetail.current_plan === planId;

                              const opts = [
                                {
                                  id: 'PROFESSIONAL',
                                  label: 'Professional Plan',
                                  price: currentPrices.PROFESSIONAL,
                                  desc: `Standard ₹${currentPrices.PROFESSIONAL.toLocaleString('en-IN')}/yr`,
                                  isCurrent: isCurrentPlan('PROFESSIONAL')
                                },
                                {
                                  id: 'PREMIUM',
                                  label: 'Premium Plan',
                                  price: currentPrices.PREMIUM,
                                  desc: `Standard ₹${currentPrices.PREMIUM.toLocaleString('en-IN')}/yr`,
                                  isCurrent: isCurrentPlan('PREMIUM')
                                }
                              ];
                              return opts.map(opt => {
                                const isSelected = manualForm.plan_name === opt.id;
                                return (
                                  <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => setManualForm({ ...manualForm, plan_name: opt.id, amount: opt.price })}
                                    className={`p-3.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col relative ${
                                      isSelected
                                        ? 'border-[#2ab391] bg-emerald-50/30 ring-2 ring-[#2ab391]/20 shadow-sm'
                                        : 'border-gray-200 hover:bg-gray-50 bg-white'
                                    }`}
                                  >
                                    {opt.isCurrent && (
                                      <span className="absolute top-2.5 right-2.5 px-1.5 py-0.2 bg-violet-100 text-violet-800 text-[8px] font-extrabold rounded-md uppercase tracking-wider border border-violet-200">
                                        Purchased Plan
                                      </span>
                                    )}
                                    <span className={`text-xs font-extrabold ${isSelected ? 'text-[#2ab391]' : 'text-gray-800'}`}>
                                      {opt.label}
                                    </span>
                                    <span className="text-[11px] text-gray-500 font-bold mt-1">{opt.desc}</span>
                                    <span className="text-[10px] text-gray-400 font-medium mt-0.5">
                                      {opt.id === 'PREMIUM'
                                        ? (isInd ? 'Gallery & Trusted Badge Included' : 'All VIP Features & Gallery')
                                        : 'Ranked Priority Placement'}
                                    </span>
                                  </button>
                                );
                              });
                            })()}
                          </div>
                        </div>

                        {/* Price charged */}
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Manually Charged Price (₹ INR)</label>
                          <input
                            type="number"
                            required
                            min="0"
                            value={manualForm.amount}
                            onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })}
                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-[#2ab391]"
                          />
                          <p className="text-[10px] text-gray-500 font-medium mt-1.5">Standard price is pre-populated. Enter custom discount price (e.g. 10000 for ₹2,000 discount).</p>
                        </div>

                        {/* Fixed Validity Duration Note */}
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Validity Duration</label>
                          <input
                            type="text"
                            disabled
                            value="1 Year (365 Days) - Fixed"
                            className="w-full bg-gray-100 border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold text-gray-500 cursor-not-allowed"
                          />
                        </div>

                        {/* Reason notes */}
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Provisioning Notes / Reason</label>
                          <textarea
                            value={manualForm.reason}
                            onChange={(e) => setManualForm({ ...manualForm, reason: e.target.value })}
                            placeholder="E.g. Approved promotional discount for Mumbai regional partner."
                            rows="2"
                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-[#2ab391]"
                          />
                        </div>

                        {manualFormError && (
                          <div className="text-xs font-bold text-red-500 bg-red-50 border border-red-100 p-3 rounded-xl select-none">
                            {manualFormError}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Modal Footer actions */}
              <div className="flex justify-end gap-3 p-6 border-t border-gray-100 bg-slate-50/50 rounded-b-2xl select-none">
                {isConfirmingGrant ? (
                  <>
                    <button
                      type="button"
                      disabled={manualFormLoading}
                      onClick={() => setIsConfirmingGrant(false)}
                      className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-50"
                    >
                      Go Back
                    </button>
                    <button
                      type="button"
                      disabled={manualFormLoading}
                      onClick={handleConfirmGrant}
                      className="px-5 py-2.5 bg-[#2ab391] hover:bg-[#229b7c] text-white rounded-lg text-xs font-bold flex items-center gap-2 cursor-pointer shadow-md shadow-[#2ab391]/10 disabled:opacity-50"
                    >
                      {manualFormLoading ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          <span>Granting...</span>
                        </>
                      ) : (
                        'Yes, Confirm Grant'
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setShowGrantManualModal(false);
                        setManualForm({ vendor_email: '', plan_name: 'PROFESSIONAL', amount: 12000, reason: '' });
                        setSelectedVendorDetail(null);
                        setIsConfirmingGrant(false);
                      }}
                      className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-50 cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleProceedToConfirmation}
                      className="px-4 py-2 bg-[#2ab391] hover:bg-[#229b7c] text-white rounded-lg text-xs font-bold flex items-center gap-2 cursor-pointer shadow-md shadow-[#2ab391]/10"
                    >
                      Grant Membership
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Membership Modal */}
      {showEditManualModal && editingManualLog && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-200 text-left">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-100 select-none">
              <div>
                <h3 className="text-base font-bold text-gray-800">Edit Manual Membership</h3>
                <p className="text-xs text-gray-400 mt-0.5">Update provisioning details for {editingManualLog.company_name}.</p>
              </div>
              <button
                onClick={() => {
                  setShowEditManualModal(false);
                  setEditingManualLog(null);
                }}
                className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleEditManualSubmit} className="flex flex-col max-h-[85vh]">
              {/* Scrollable Form Body */}
              <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh] flex-1">
                {/* Vendor Display */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Vendor Details</label>
                  <div className="bg-slate-50 border border-gray-200 p-4 rounded-xl">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-extrabold text-gray-800 text-sm">{editingManualLog.company_name}</span>
                      <span className={`px-2 py-0.5 text-[9px] font-bold rounded-md uppercase tracking-wider ${
                        (editingManualLog.listing_type || editingManualLog.listingType) === 'INDIVIDUAL'
                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                          : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                      }`}>
                        {(editingManualLog.listing_type || editingManualLog.listingType) === 'INDIVIDUAL' ? 'Individual Listing' : 'Company Listing'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 font-semibold mt-0.5">{editingManualLog.vendor_email}</div>
                  </div>
                </div>

                {/* Plan choice */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Select Override Plan</label>
                  <div className="grid grid-cols-2 gap-3">
                    {(() => {
                      const isInd = editingManualLog && ((editingManualLog.listing_type || editingManualLog.listingType) === 'INDIVIDUAL');
                      const currentPrices = isInd ? planPrices.individual : planPrices.company;
                      const opts = [
                        {
                          id: 'PROFESSIONAL',
                          label: 'Professional Plan',
                          price: currentPrices.PROFESSIONAL,
                          desc: `Standard ₹${currentPrices.PROFESSIONAL.toLocaleString('en-IN')}/yr`,
                          features: 'Ranked Priority Placement'
                        },
                        {
                          id: 'PREMIUM',
                          label: 'Premium Plan',
                          price: currentPrices.PREMIUM,
                          desc: `Standard ₹${currentPrices.PREMIUM.toLocaleString('en-IN')}/yr`,
                          features: isInd ? 'Gallery & Trusted Badge Included' : 'All VIP Features & Gallery'
                        }
                      ];
                      return opts.map(opt => {
                        const isSelected = editManualForm.plan_name === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setEditManualForm({ ...editManualForm, plan_name: opt.id, amount: opt.price })}
                            className={`p-3.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col ${
                              isSelected
                                ? 'border-[#2ab391] bg-emerald-50/30 ring-2 ring-[#2ab391]/20 shadow-sm'
                                : 'border-gray-200 hover:bg-gray-50 bg-white'
                            }`}
                          >
                            <span className={`text-xs font-extrabold ${isSelected ? 'text-[#2ab391]' : 'text-gray-800'}`}>
                              {opt.label}
                            </span>
                            <span className="text-[11px] text-gray-500 font-bold mt-1">{opt.desc}</span>
                            <span className="text-[10px] text-gray-400 font-medium mt-0.5">
                              {opt.features}
                            </span>
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Price charged */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Charged Price (₹ INR)</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={editManualForm.amount}
                    onChange={(e) => setEditManualForm({ ...editManualForm, amount: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-[#2ab391]"
                  />
                </div>

                {/* Custom Expiry Date */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Validity End Date</label>
                  <DatePicker
                    value={editManualForm.end_date}
                    onChange={(val) => setEditManualForm({ ...editManualForm, end_date: val })}
                    placeholder="Select Expiry Date"
                    className="w-full bg-gray-50/50 hover:bg-slate-50 border-gray-300 font-semibold text-gray-800"
                    allowFuture={true}
                  />
                </div>

                {/* Reason notes */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Update Reason / Notes</label>
                  <textarea
                    value={editManualForm.reason}
                    onChange={(e) => setEditManualForm({ ...editManualForm, reason: e.target.value })}
                    placeholder="E.g. Corrected custom billing amount or extended promo duration."
                    rows="2"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-[#2ab391]"
                  />
                </div>

                {editManualFormError && (
                  <div className="text-xs font-bold text-red-500 bg-red-50 border border-red-100 p-3 rounded-xl select-none">
                    {editManualFormError}
                  </div>
                )}
              </div>

              {/* Modal Footer actions */}
              <div className="flex justify-end gap-3 p-6 border-t border-gray-100 bg-slate-50/50 rounded-b-2xl select-none">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditManualModal(false);
                    setEditingManualLog(null);
                  }}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editManualFormLoading}
                  className="px-4 py-2 bg-[#2ab391] hover:bg-[#229b7c] text-white rounded-lg text-xs font-bold flex items-center gap-2 cursor-pointer shadow-md shadow-[#2ab391]/10 disabled:opacity-50"
                >
                  {editManualFormLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualMembershipsTab;
